import React, { useRef } from 'react';
import { LoadingSpinner, ErrorMessage, EmptyState, HorizontalTableShell } from '../ui';
import { useMainPerformanceSmsChargeMonthly, SectionProps } from '../../hooks/useMainPerformanceSections';
import { formatNumber } from '../../utils/formatters';
import ChartControls from '../ui/ChartControls';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import useChartGuide from '../../hooks/useChartGuide';
import { CHART_GUIDES } from '../../constants/chartGuides';

// Color-code the day count: green when fully charged, amber for a few gaps,
// red when the branch was without SMS charge for many days in the month.
const getDaysBadgeClass = (days: number): string => {
  if (days <= 0) return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
  if (days >= 5) return 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300';
  return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300';
};

const DaysBadge: React.FC<{ days: number }> = ({ days }) => (
  <span className={`inline-block min-w-[64px] rounded-full px-4 py-1.5 text-sm font-bold ${getDaysBadgeClass(days)}`}>
    {formatNumber(days)} روز
  </span>
);

const TABLE_ROW_HEIGHT_PX = 60;
const TABLE_HEADER_HEIGHT_PX = 56;
const TABLE_VISIBLE_ROWS = 8;

interface SmsChargeMonthlyTableProps extends SectionProps {
  branchId: number;
  timeRange?: string;
}

const SmsChargeMonthlyTable: React.FC<SmsChargeMonthlyTableProps> = ({
  branchId,
  timeRange = 'last6Months',
  onError,
  showLoading = true,
  compact = false
}) => {
  const isTwelveMonthsView = timeRange === 'lastYear' || timeRange === 'last12Months';
  const chartRef = useRef<HTMLDivElement>(null);
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { openGuide } = useChartGuide();
  const { data, isLoading, isError, error, refetch } = useMainPerformanceSmsChargeMonthly(branchId, timeRange);

  const componentId = 'sms-charge-monthly-table';
  const componentTitle = 'روزهای بدون شارژ پیامک به تفکیک ماه';

  React.useEffect(() => {
    if (isError && error && onError) {
      onError(error as Error);
    }
  }, [isError, error, onError]);

  if (isLoading && showLoading) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <div className="flex flex-col items-center justify-center h-[200px]">
          <LoadingSpinner
            variant="minimal"
            size="lg"
            message="در حال بارگذاری جدول روزهای بدون شارژ پیامک…"
          />
        </div>
      </div>
    );
  }

  if (isError && error) {
    return (
      <div className="card-base card-padding">
        <ErrorMessage
          message="خطا در دریافت اطلاعات روزهای بدون شارژ پیامک"
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  // Show the most recent month first.
  const months = [...(data?.months || [])].reverse();

  if (months.length === 0) {
    return (
      <div className="card-base card-padding">
        <EmptyState />
      </div>
    );
  }

  const totalDays = data?.totalDays || 0;

  return (
    <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
      <ChartControls
        componentId={componentId}
        componentTitle={componentTitle}
        componentType="table"
        dashboardSource="mainPerformance"
        onInfo={() => openGuide(CHART_GUIDES.smsChargeMonthlyTable)}
        onFavorite={() => handleFavoriteToggle({
          componentId,
          componentTitle,
          dashboardSource: 'mainPerformance',
          componentType: 'table',
          description: 'تعداد روزهای بدون شارژ پیامک هر ماه به همراه مجموع کل دوره',
          props: { branchId, timeRange, compact },
          gridLayout: {
            containerClass: 'grid grid-cols-1 gap-6'
          }
        })}
        isFavorited={isFavorited(componentId)}
        chartRef={chartRef}
        className="mb-4"
      />

      {/* Title + subtitle, centered like the reference design */}
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-text-primary">روزهای بدون شارژ پیامک</h3>
        <p className="mt-3 text-sm text-text-secondary">
          تعداد روزهایی که در هر ماه شارژ پیامک منفی شده است
        </p>
      </div>

      <HorizontalTableShell
        minWidth={480}
        scrollClassName={isTwelveMonthsView ? 'overflow-y-auto' : ''}
        scrollStyle={
          isTwelveMonthsView
            ? { maxHeight: TABLE_HEADER_HEIGHT_PX + TABLE_VISIBLE_ROWS * TABLE_ROW_HEIGHT_PX }
            : undefined
        }
      >
          <table className="w-full border-collapse" style={{ direction: 'rtl' }}>
            <thead>
              <tr
                className="border-b-2 !bg-gray-100 dark:!bg-gray-800"
                style={{ borderColor: 'var(--border-secondary)' }}
              >
                <th
                  className="px-3 py-4 text-center text-sm font-bold border-l last:border-l-0 text-text-primary"
                  style={{ borderColor: 'var(--border-primary)' }}
                >
                  ماه
                </th>
                <th
                  className="px-3 py-4 text-center text-sm font-bold border-l last:border-l-0 text-text-primary"
                  style={{ borderColor: 'var(--border-primary)' }}
                >
                  تعداد روزهای بدون شارژ
                </th>
              </tr>
            </thead>
            <tbody>
              {months.map((m, index) => (
                <tr
                  key={m.yearMonth}
                  className={`transition-all border-b ${index % 2 === 0 ? '!bg-white dark:!bg-gray-900' : '!bg-gray-50 dark:!bg-gray-800'} hover:!bg-blue-50 dark:hover:!bg-gray-700`}
                  style={{ borderColor: 'var(--border-primary)' }}
                >
                  <td
                    className="px-3 py-4 whitespace-nowrap text-center text-sm font-bold border-l last:border-l-0 text-text-primary bg-bg-tertiary"
                    style={{ borderColor: 'var(--border-primary)' }}
                  >
                    {m.month}
                  </td>
                  <td
                    className="px-3 py-4 whitespace-nowrap text-center text-sm border-l last:border-l-0"
                    style={{ borderColor: 'var(--border-primary)' }}
                  >
                    <DaysBadge days={m.daysWithoutCharge} />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr
                className="border-t-2 bg-primary-100 dark:bg-primary-900/30"
                style={{ borderColor: 'var(--border-primary)' }}
              >
                <td
                  className="px-3 py-4 whitespace-nowrap text-center text-sm font-bold border-l last:border-l-0 text-primary-700 dark:text-primary-300"
                  style={{ borderColor: 'var(--border-primary)' }}
                >
                  مجموع
                </td>
                <td
                  className="px-3 py-4 whitespace-nowrap text-center text-sm font-bold border-l last:border-l-0 text-primary-700 dark:text-primary-300"
                  style={{ borderColor: 'var(--border-primary)' }}
                >
                  {formatNumber(totalDays)} روز
                </td>
              </tr>
            </tfoot>
          </table>
      </HorizontalTableShell>
    </div>
  );
};

export default SmsChargeMonthlyTable;
